import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, Link } from "react-router-dom"
import Card from "react-bootstrap/Card";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Tooltip from "react-bootstrap/Tooltip";
import Popover from "react-bootstrap/Popover";
import { useDebounceCallback } from '@react-hook/debounce'
import { useInView } from 'react-intersection-observer'
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import classes from "./ExerciseList.module.css";
import Select from 'react-select'
import {envAwarePost,slidingQueryComparision} from '../../util/util'
import useApiStatusDispatch from "../../hooks/useApiStatusDispatch";
import { useLibraryExercises } from './hooks'
import { selectTrainerParam } from '../users/usersSlice'
import CenteredLoader from '../CenteredLoader/CenteredLoader'
import {
	addExercise,
	editExercise,
	deleteExercise,
	getExerciseList,
	getLibraryExercises,
	getLibraryExercisesAfterAdding,
	getAdditionalExerciseData,
	getNumRemainingDemoVideos,
	deleteAllExercises,
	addAllFitswDefaults,
	deleteMultipleExercises
} from "./exerciseListSlice";
import Exercise from './Exercise'
import MultiSelect from "react-multi-select-component";
import { DebounceInput } from "react-debounce-input";
import BulkCopyModal from './CopyModal'
import useS3DirectUpload from '../../hooks/useS3DirectUpload'

const colorArray = [
	"success",
	"primary",
	"danger",
	"info",
	"warning",
	"dark",
];

const getColorMapForExercises = exercises => {
	const uniqueMuscleGroups = Array.from(new Set(exercises.map(e => (
		e.muscleGroupData.muscle_group
	)))).sort((a, b) => a < b ? -1 : 1)
	const map = {}
	uniqueMuscleGroups.forEach((g, i) => {
		map[g] = colorArray[i % colorArray.length]
	})
	return map
}

const ExerciseList = () => {
	const dispatch = useDispatch();
	const user = useSelector(state => state.users.current_user);
	const clientId = useSelector((state) => state.users.current_user.user_id_number)
	const isClient = useSelector((state) => state.users.current_user.account_type) === "client"
	const isSharedClient = isClient && user.trainer_data.trainer_id == user.gym_id
	const isTrainer = useSelector((state) => state.users.current_user.account_type) === "trainer"
	const isGymTrainer = useSelector((state) => state.users.current_user.account_type === "trainer" && state.users.current_user.gym_id.length > 1)
	const isTrainerOrGym = useSelector((state) => state.users.current_user.account_type === "trainer" || state.users.current_user.account_type === "gym")
	const isPartOfGym = user.gym_id != 0
	const isChain = user.account_type === 'chain'
	const trainerParam = useSelector(selectTrainerParam)
	let clientCanEdit = (user.edit_permissions.exercise === "1") && isClient;
	const [uploadVideoChosen, setUploadVideoChosen] = useState(false)
	const history = useHistory()
	//use the current user's library
	const { status, exercises } = useLibraryExercises(user.user_id_number)
	const colorMap = useMemo(() => getColorMapForExercises(exercises), [exercises])
	//search exercises
	const [searchQuery, setSearchQuery] = useState("");
	//to allow debouncing the search && controlling the input
	//we need two bits of local state
	const [searchQueryText, setSearchQueryText] = useState("")
	const [addAllExercisesLoading, setAddAllExercisesLoading] = useState(false);

	const [showBulkCopyModal,setShowBulkCopyModal] = useState(false)

	//list of gyms for corporate account
	const [gyms,setGyms] = useState([])
	const [selectedAccount,setSelectedAccount] = useState({label:'Corporate List',value:user.user_id_number})

	useEffect(()=>{
		envAwarePost('api/users/',{action:'getAllGyms'}).then(res=>{
			setGyms(res.data)
		})
	},[])

	const [hideDefaults,setHideDefaults] = useState(false)

	//Filters
	//Text Filter
	const [filterText, setFilterText] = useState("");

	//Muscle Group Filters
	const [muscleFilter, setMuscleFilter] = useState([]);

	//Level Filters
	const [levelFilter, setLevelFilter] = useState([]);

	//Equipment Filters
	const [equipmentOptions, setEquipmentOptions] = useState([]);
	const [equipmentSelected, setEquipmentSelected] = useState([]);

	const [isUploading, setIsUploading] = useState(false)

	const [isDeletingExercises, setIsDeletingExercises] = useState(false)
	// owner type filter default to all
	const [ownerFilter, setOwnerFilter] = useState({ label: 'all exercises', value: 'all' });


	const matchesFilters = e => {

		const selectedMuscleGroups = muscleFilter.map(f => f.label)
		const muscleGroupMatch = (
			selectedMuscleGroups.length > 0 ?
				selectedMuscleGroups.includes(e.muscleGroupData.muscle_group)
				:
				//if no filers true by default
				true
		)

		const selectedLevels = levelFilter.map(f => f.label)
		const levelMatch = (
			selectedLevels.length > 0 ?
				selectedLevels.includes(e.levelData.exerciseLevel)
				:
				//if no filters true by default
				true
		)

		const selectedEquipment = new Set(equipmentSelected.map(f => f.label))
		const exerciseEquipment = e.equipmentData.map(e => e.name)
		const equipmentMatch = (
			equipmentSelected.length > 0 ?
				exerciseEquipment.reduce(
					(carry, item) => (carry && selectedEquipment.has(item)),
					//this is needed to prevent exercises with no equipment
					//for getting matched
					exerciseEquipment.length > 0
				)
				:
				//if no filers true by default
				true
		)

		const ownerMatch = (
			e.owner_type === ownerFilter.value ||
			ownerFilter.value === 'all'
		)

		const accountMatch = (
			isChain
			? 
			(
				//only show exercises for selected account
				parseInt(e.user_id_number) === parseInt(selectedAccount.value) || 
				//or if a corporate account is viewing their own list also show the defaults
				(parseInt(e.user_id_number) === 999999 && parseInt(selectedAccount.value) === parseInt(user.user_id_number))
			)
			: true
		)

		const defaultMatch = (
			!hideDefaults
			|| parseInt(e.user_id_number) !== 999999
		)

		const isMatch = (
			//searchQueryMatch &&
			muscleGroupMatch 
			&& levelMatch 
			&& ownerMatch 
			&& equipmentMatch 
			&& accountMatch 
			&& defaultMatch
		)

		return isMatch
	}

	//reset the display count everytime the fitlers or search query or list change
	useEffect(() => {
		setDisplayCount(exerciseBlockSize)
	}, [ownerFilter, levelFilter, equipmentSelected, muscleFilter, levelFilter])

	const [reverseSort, setReverseSort] = useState(false)

	const clearFilters = () => {
		setSearchQuery("")
		setSearchQueryText("")
		setMuscleFilter([])
		setLevelFilter([])
	}

	const hasFilters = () => (
		Boolean(searchQuery) ||
		muscleFilter.length > 0 ||
		levelFilter.length > 0 ||
		equipmentSelected.length > 0
	)

	const generateFilterDisplayText = () => {
		const filterTextItems = []
		if (Boolean(searchQuery)) filterTextItems.push(`matching: "${searchQuery}"`)
		if (muscleFilter.length > 0) filterTextItems.push(`muscles: ${muscleFilter.map(f => f.label).join(', ')}`)
		if (levelFilter.length > 0) filterTextItems.push(`levels: ${levelFilter.map(f => f.label).join(', ')}`)
		if (equipmentSelected.length > 0) filterTextItems.push(`equipment: ${equipmentSelected.map(f => f.label).join(', ')}`)
		return (
			filterTextItems.length > 0 ?
				`Filtered results - ${filterTextItems.join(' | ')}`
				:
				''
		)
	}

	const exerciseBlockSize = 60
	const [displayCount, setDisplayCount] = useState(exerciseBlockSize)
	//conctatenates all relevant exercise strings into single searchable
	//string to use with the sliding query comparison function
	const exerciseSearchString = (e)=>{
		//use \n because: '\n' < ' ' is true
		//this way alphabetical sort works as expected
		//when two strings both match with the search query
		//at the start
		return [
			String(e.exercise_db_name).toLowerCase(),
			String(e.notes).toLowerCase(),
			String(e.muscleGroupData.muscle_group).toLowerCase(),
			e.equipmentData.map(e=>e.name).join('\n').toLowerCase()
		].filter(s=>!!s).join('\n')
	}

	const filteredExercises = useMemo(
		() => exercises.filter(matchesFilters).sort((a, b) => {
			const multiplier = reverseSort ? -1 : 1
			const aName = (a.name || "").toLowerCase()
			const bName = (b.name || "").toLowerCase()
			//when a search query is present the sorting is based on query
			//relevance smaller relevance values are better. In the sense
			//that they differ less from the intent of the search
			if (Boolean(searchQuery)) {
				const delta = slidingQueryComparision(
					exerciseSearchString(a),
					exerciseSearchString(b),
					searchQuery.toLowerCase()
				)
				const normalSortValue = delta
				const toggledSortValue = normalSortValue * multiplier
				return toggledSortValue
			} else {

				const aMuscleGroup = (a.muscleGroupData.muscle_group || "").toLowerCase()
				const bMuscleGroup = (b.muscleGroupData.muscle_group || "").toLowerCase()

				const normalSortValue = (aMuscleGroup === bMuscleGroup ?
					aName < bName ? -1 : 1 :
					aMuscleGroup < bMuscleGroup ? -1 : 1
				)
				const toggledSortValue = normalSortValue * multiplier
				return toggledSortValue
			}

		}),
		[searchQuery, muscleFilter, levelFilter, ownerFilter, exercises, reverseSort, equipmentSelected,selectedAccount,hideDefaults]
	);
	const filteredExercisesSlice = useMemo(
		() => filteredExercises.slice(0, displayCount),
		[filteredExercises, reverseSort, displayCount,hideDefaults]
	)

	const [showFilterPopover, setShowFilterPopover] = useState(false);

	//Creating object to map level strings with IDs, since most levelData objects have blank IDs for some reason
	const levelDataMap = {
		Beginner: 1,
		Intermediate: 2,
		Advanced: 3,
		None: 4,
	};

	useEffect(() => {
		dispatch(getAdditionalExerciseData());
	}, []);

	//Add Exercise Stuff
	const [showAdd, setShowAdd] = useState(false);
	const handleAddClose = () => setShowAdd(false);
	const handleAddShow = () => setShowAdd(true);
	const [addExName, setAddExName] = useState("");
	const [addExEqSelected, setAddExEqSelected] = useState([]);
	const [addExLevel, setAddExLevel] = useState("");
	const [addExMuscleGrp, setAddExMuscleGrp] = useState("");
	const [addExDemoLink, setAddExDemoLink] = useState("");
	const [addExPreviewImg, setAddExPreviewImg] = useState("");

	const [addExDemoVideo, setAddExDemoVideo] = useState("");
	const [addVideoOperation, setAddVideoOperation] = useState(false);
	const [addToGymLibrary, setAddToGymLibrary] = useState(true);
	const [addExNotes, setAddExNotes] = useState("");
	const [addImgOperation, setAddImgOperation] = useState(false);
	const [isUploadedEditVideo, setIsUploadedEditVideo] = useState(false)

	const clearAddModalVar = () => {
		setUploadVideoChosen(false);
		setAddExName("");
		setAddExEqSelected([]);
		setAddExLevel("");
		setAddExMuscleGrp("");
		setAddExDemoLink("");
		setAddExDemoVideo("");
		setAddExPreviewImg("");
		setAddExNotes("");
		setAddImgOperation(false);
		setAddVideoOperation(false);
	};

	const { ref: scrollRef, inView } = useInView({
		/* Optional options */
		threshold: 0,
	});

	useEffect(() => {
		if (inView && displayCount < filteredExercises.length) {
			setDisplayCount(displayCount + exerciseBlockSize)
		}
	}, [inView])

	//Edit Exercise Modal
	const [showEdit, setShowEdit] = useState(false);
	const handleEditClose = () => {
		setShowEdit(false);
		clearEditModalVar();
		setEditExPreviewImgLink('')
		setIsUploadedEditVideo(false)
	}

	const handleEditShow = () => setShowEdit(true);
	const [editExId, setEditExId] = useState("");
	const [editExName, setEditExName] = useState("");
	const [editExEqSelected, setEditExEqSelected] = useState([]);
	const [editExLevel, setEditExLevel] = useState("");
	const [editExMuscleGrp, setEditExMuscleGrp] = useState("");
	const [editExDemoLink, setEditExDemoLink] = useState("");
	const [editExPreviewImg, setEditExPreviewImg] = useState("");
	const [editExDemoVideo, setEditExDemoVideo] = useState("");
	const [removeExPreviewImg, setRemoveExPreviewImg] = useState(false)
	const [updateAllWorkouts, setUpdateAllWorkouts] = useState(false)
	const [removeExDemoVideo, setRemoveExDemoVideo] = useState(false)
	const [editExNotes, setEditExNotes] = useState("")
	const [editImgOperation, setEditImgOperation] = useState(false)
	const [editVideoOperation, setEditVideoOperation] = useState(false)
	const [editExPreviewImgLink, setEditExPreviewImgLink] = useState('')

	const [editExDefault, setEditExDefault] = useState(0)
	const statusDispatch = useApiStatusDispatch()

	const clearEditModalVar = () => {
		setUploadVideoChosen(false);
		setEditExId("");
		setEditExName("");
		setEditExEqSelected([]);
		setEditExLevel("");
		setEditExMuscleGrp("");
		setEditExDemoLink("");
		setEditExPreviewImg("");
		setEditExDemoVideo("");
		setRemoveExDemoVideo(false);
		setRemoveExPreviewImg(false);
		setEditExNotes("");
		setEditImgOperation(false);
		setEditVideoOperation(false);
		setEditExDefault(0);
	}

	const openEditModal = (exercise) => {
		setEditExId(exercise.id);
		setEditExName(exercise.name);
		setEditExLevel({
			...exercise.levelData,
			id: exercise.exerciseLevelID,
		});
		setEditExMuscleGrp(exercise.muscleGroupData);
		setEditExEqSelected(
			exercise.equipmentData.map((eq) => ({ label: eq.name, value: eq.id }))
		);
		setEditExDemoLink(exercise.demoLink);
		setEditExNotes(exercise.notes);
		setEditExPreviewImg(exercise.previewData?.src);
		setEditExDemoVideo(exercise.demo_video);
		if (exercise.demo_video != "") {
			setUploadVideoChosen(true);
		}
		setAddExDemoVideo();
		setEditExDefault(exercise.isDefault);
		handleEditShow(true);

	}

	const exerciseList = useSelector((state) => state.exercise.exerciseList)

	const additionalExerciseData = useSelector(
		(state) => state.exercise.additionalExerciseData
	)

	const editVideoRef = useRef(null)
	const addVideoRef = useRef(null)
	const imgRef = useRef(null);

	const removeFilters = () => {
		setFilterText("")
		setMuscleFilter("")
		setLevelFilter("")
		setEquipmentSelected("")
	}

	const mapMuscleGroupToSelectObjects = equipmentList => (
		equipmentList && equipmentList.map(equipment => ({
			label: equipment.name,
			value: equipment.id
		}))
	)//end map

	const mapFiltersToSelectObjects = filters => (
		filters && filters.map(filter => ({
			label: filter.name,
			value: filter.name
		}))
	)//end map

	function possibleOwners() {
		let ownerList = [];
		exercises.map(e => {
			if (!ownerList.includes(e.owner_type)) {
				ownerList.push(e.owner_type)
			}
		})

		ownerList = ownerList.map(item => {
			return {
				label: item == user.account_type ? "my exercises" : item + " exercises",
				value: item
			}
		})
		ownerList.push({
			label: "all exercises",
			value: 'all'
		})
		return ownerList;
	}

	const debouncedSetSearchQuery = useDebounceCallback(setSearchQuery, 500)

	//search exercises
	const handleSearchQuery = e => {
		const v = e.target.value
		debouncedSetSearchQuery(v)
		setSearchQueryText(v)
		//reset the display count to 100
		setDisplayCount(exerciseBlockSize)
	}

	const handleGraphBtnClick = () => {
		history.push(`/exercise_progress/?client=${clientId}`)
		// history.go(0)
	}

	const handleAddAddAllExercises = () => {
		setAddAllExercisesLoading(true);
		dispatch(addAllFitswDefaults())
			.then(res => {
				dispatch(getLibraryExercisesAfterAdding({ tgt_user_id_number: user.user_id_number }))
					.then(res => setAddAllExercisesLoading(false))
			})
	}

	const [selectedList, setSelectedList] = useState([])

	//delete multiple exercises
	const handleDeleteMultipleExercises = (e) => {
		let confirmText = selectedList.length > 1 ?
			`Are you sure you want to delete these ${selectedList.length} exercises? This will affect all workouts that use these exercises.` :
			"Are you sure you want to delete this exercises? This will affect all workouts that use this exercise."
		if (!window.confirm(confirmText)) return
		statusDispatch(deleteMultipleExercises({
			ids: selectedList,
		}), selectedList.length > 1 ? "exercises" : "exercise", "delete")
		.then(
			setSelectedList([])
		)
	}

	//delete exercises
	const handleDeleteExercise = exercise => {
		if (!window.confirm('Are you sure you want to delete? This will affect all workouts that use the exercise(s).')) return
		statusDispatch(deleteExercise({
			action: "deleteExercise",
			id: exercise.id,
		}), "exercise", "delete")
	}

	const handleAddSelected = selectedItem => {
		if (selectedList.includes(selectedItem)) {
			let newList = selectedList.filter(item => item != selectedItem)
			setSelectedList(newList)
		} else {
			setSelectedList(prev => [...prev, selectedItem])
		}
	}

	const handleDeleteAll = () => {
		if (!window.confirm('Are you sure you want to DELETE ALL EXERCISES?')) return
		statusDispatch(deleteAllExercises(), "exercises", "delete")
	}

	const {processUpload} = useS3DirectUpload()

	const filterPopover = (
		<Popover>
			<Popover.Content >
				<div>
					{/* Filter By Muscle Multi Select */}
					<div className="d-flex flex-wrap justify-content-between align-items-stretch">
						<div className="mb-1 w-100">
							<div>
								<label>
									Filter by Muscle Group
								</label>
							</div>
							<MultiSelect
								options={mapMuscleGroupToSelectObjects(additionalExerciseData.muscle_groups) || []}
								value={muscleFilter}
								onChange={setMuscleFilter}
								labelledBy="Select"
								hasSelectAll={false}
							/>
						</div>
						<div className="w-100 mb-1">
							<div>
								<label>Filter by levels</label>
							</div>
							<MultiSelect
								id="filter-group-select"
								value={levelFilter}
								options={mapFiltersToSelectObjects(additionalExerciseData.exercise_levels) ? mapFiltersToSelectObjects(additionalExerciseData.exercise_levels) : []}
								onChange={(e) => setLevelFilter(e)}
								hasSelectAll={false}
								isMulti
							/>

						</div>
						{/* Equipment Filter */}
						{/* Equipment data does not show up in local */}
						<div className="w-100">
							<label>Filter by Equipment</label>
							<MultiSelect
								options={mapFiltersToSelectObjects(additionalExerciseData.equipment)}
								value={equipmentSelected}
								hasSelectAll={false}
								onChange={(e) => {
									setEquipmentSelected(e);
								}}
							/>
						</div>

					</div>
					<div className="d-flex justify-content-start mt-2">
						<Button
							size="sm"
							variant="outline-danger"
							onClick={clearFilters}
						>
							Clear

						</Button>
					</div>
				</div>
			</Popover.Content>
		</Popover >
	)

	const showDeleteAll = (
		user.account_type == ownerFilter.value ||
		(user.gym_id == 0 && user.account_type === 'trainer') ||
		user.account_type === 'gym'
	)

	return (
		<div className={classes.exerciseListContainer} style={{ paddingBottom: '50px' }} id="containerDiv">
			<div className="pt-2 px-2 px-md-3">
				<strong style={{ marginBottom: '0px', lineHeight: '1.5' }}>
					Exercise Library
				</strong>
				<div>
					<div className="rounded mb-3 w-100">
						<div>
							<div className="d-flex w-100 pt-2">
								<OverlayTrigger
									trigger="click"
									show={showFilterPopover}
									onHide={() => setShowFilterPopover(false)}
									placement="right"
									overlay={filterPopover}
								>
									<OverlayTrigger overlay={
										<Tooltip>
											Exercise Filter
										</Tooltip>
									}>
										<Button
											onClick={() => setShowFilterPopover(!showFilterPopover)}
											size="sm"
											variant={showFilterPopover ? "outline-danger" : "outline-primary"}
										>
											<i className={showFilterPopover ? "fas fa-times-circle" : "fas fa-filter"} />
										</Button>
									</OverlayTrigger>
								</OverlayTrigger>
								<Form.Control
									onChange={handleSearchQuery}
									value={searchQueryText}
									placeholder="search exercises"
									className="mx-1"
								/>

								{(isGymTrainer || isClient) &&
									<div style={{ position: 'relative', flex: '0 0 140px' }}>
										<small style={{
											position: 'absolute',
											top: 0,
											left: 0,
											transform: 'translateY(-100%)'
										}}>
											Select list
										</small>
										<Select
											style={{ textTransform: "capitalize" }}
											options={
												possibleOwners()}
											value={ownerFilter}
											onChange={e =>
												setOwnerFilter(e)}
											labelledBy="Select"
										/>
									</div>
								}
								{isChain&&
									<div style={{flex:'0 0 auto',width:'180px'}}>
										<Select
											onChange={setSelectedAccount}
											value={selectedAccount}
											options={[
												{label:'Corporate List',value:user.user_id_number},
												...gyms.map(g=>({value:g.user_id_number,label:g.user_name}))
											]}
										/>
									</div>
								}
							</div>
							{!isClient &&
								<div className="mt-2">
									<Link to={`/muscleGroups?${trainerParam}`}
									>
										Add New Muscle Group
									</Link>
								</div>
							}
							<div className="d-flex justify-content-between align-items-center flex-wrap ml-1">
								<div className="mt-3 d-flex">
									<Form.Check
										onChange={e => setReverseSort(e.target.checked)}
										label="Reverse List"
										id="reverse-sort"
										checked={reverseSort}
									/>
									<Form.Check
										className="ml-2"
										onChange={e => setHideDefaults(e.target.checked)}
										label="Hide default exercises"
										id="hide-fitsw-defaults"
										checked={hideDefaults}
									/>
								</div>

								<div className="mt-1">
									{((clientCanEdit && !isSharedClient) || !isClient)
										&& exercises?.length < 5 &&
										status !== 'pending' &&
										<div style={{ display: "inline-block" }}>

											<Button
												onClick={handleAddAddAllExercises}
												disabled={addAllExercisesLoading}
												variant="outline-primary"
												className="mr-1 mt-2"
												size="sm"
											>
												{addAllExercisesLoading ?
													<Spinner size="sm" animation="border" variant="primary" role="status" />
													:
													"Add All FitSW's Default Exercises"}
											</Button>
										</div>

									}
									{showDeleteAll &&
										<Button
											onClick={handleDeleteAll}
											variant="outline-danger"
											className="mr-3 mt-2"
											size="sm"
										>
											Delete all of my exercises
										</Button>
									}
									{(isClient && exercises.length > 0) &&
										<Button size="sm" className="mr-1 mt-2" variant="outline-primary" onClick={handleGraphBtnClick}>View Graphs</Button>
									}
									{((clientCanEdit && !isSharedClient) || !isClient) &&
										<Button
											size="sm"
											variant="outline-success"
											className="mt-0 mt-2"
											onClick={() => handleAddShow(true)}
										>
											Add Exercise
										</Button>
									}

								</div>
							</div>
							{hasFilters() &&
								<div className="mt-1 d-flex align-items-center justify-content-between flex-wrap">
									<div>{generateFilterDisplayText()}</div>
									<Button size="sm" variant="outline-danger" className="my-2"
										onClick={clearFilters}>
										Clear Filters

									</Button>
								</div>
							}
						</div>
					</div>

				</div>
			</div>
			<div className="bg-white rounded border overflow-hidden" >
				{
					status === 'pending' ?
						<CenteredLoader /> :

						<div>
							<div
								className="d-flex py-2 pl-3 pr-2 mb-1 justify-content-between"
								style={{
									backgroundColor: '#36394f',
									color: '#fff'
								}}
							>
								<span style={{ borderBottom: '1px solid #00ff8c' }}>Exercise</span>
								<div className="d-flex align-items-center">
									<span>Level</span>
									<span className="mx-1" style={{ color: '#4dd1ff' }}>|</span>
									<span>Muscle Group</span>
									{/* 
										Hide the delete option for corporate if 
										they are not viewing there own list 
									*/}
									{(isChain&&parseInt(selectedAccount.value)===parseInt(user.user_id_number)||!isChain)&&
										<Button
											className="ml-2"
											variant="outline-danger"
											disabled={selectedList?.length < 1}
											onClick={handleDeleteMultipleExercises}
											size="sm"
										>
											<i className="fas fa-trash" />
										</Button>
									}
									{isChain&&
										<Button
											className="ml-2"
											variant="outline-primary"
											disabled={selectedList?.length < 1}
											onClick={()=>{setShowBulkCopyModal(true)}}
											size="sm"
										>
											 <i className="fas fa-copy" />
										</Button>
									}
								</div>

							</div>
							{
								filteredExercisesSlice.length > 0 ?
									<div className="px-3 py-2">
										{filteredExercisesSlice.map(e => (
											<Exercise
												selected={selectedList}
												key={e.exercise_db_id}
												exercise={e}
												handleAddSelected={handleAddSelected}
												openEditModal={openEditModal}
												handleEditClose={handleEditClose}
												muscleGroupColor={colorMap[e.muscleGroupData.muscle_group]}
											/>
										)
										)}
									</div>
									:
									<div>
										{
											exercises.length > 0 ?
												'No matching exercises' :
												'You haven\'t added any exercises'
										}
									</div>
							}
						</div>
				}
			</div>
			{displayCount < filteredExercises.length &&
				<div ref={scrollRef}>
					Loading more...
				</div>
			}
			{/* ADD MODAL */}
			<Modal show={showAdd} onHide={handleAddClose}>
				<Modal.Header closeButton style={{ margin: 0 }}>
					<Modal.Title>Add Exercise</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ paddingLeft: 30, paddingRight: 30 }}>
					{Object.keys(additionalExerciseData).length > 0 && (
						<>
							<div className="flex-fill">
								<label className={classes.ModalLabel}>Exercise Name*</label>
								<DebounceInput
									minLength={1}
									debounceTimeout={300}
									onChange={(e) => setAddExName(e.target.value)}
									value={addExName}
									className="input-group"
								/>
							</div>
							<div style={{ position: "relative" }}>
								<div>
									<label
										className={classes.ModalLabel}
										htmlFor="muscle-group-select"
									>
										Muscle Group*
									</label>
								</div>
								<Select
									options={additionalExerciseData.muscle_groups.map(
										(muscleGroup) => ({
											label: muscleGroup.name,
											value: JSON.stringify({
												id: muscleGroup.id,
												name: muscleGroup.name,
											})
										})
									)}
									onChange={(e) => {
										setAddExMuscleGrp(JSON.parse(e.value));
									}}
									id="muscle-group-select"
									defaultValue=""
								/>
								{!isClient &&
									<div className="mt-2">
										<Link to="/muscleGroups" target="_blank"
										>
											Add New Muscle Group
											<i
												className="far fa-question-circle text-white"
												style={{ marginLeft: "5px" }}
											></i>
										</Link>
									</div>
								}
							</div>
							<div className="flex-fill">
								<div>
									<label className={classes.ModalLabel} htmlFor="level-select">
										Level*
									</label>
								</div>
								<Select

									id="level-select"
									options={additionalExerciseData.exercise_levels.map((level) => ({
										label: level.name,
										value:
											JSON.stringify({
												id: level.id,
												name: level.name,
											})
									})
									)}
									onChange={(e) => {
										setAddExLevel(JSON.parse(e.value));
									}}
									defaultValue=""

								/>
							</div>
							{/* Equipment data does not show up in local */}
							<div className="flex-fill">
								<label className={classes.ModalLabel}>
									Equipment (Optional)
								</label>

								<Select
									options={additionalExerciseData.equipment.map((equip) => ({
										label: equip.name,
										value: equip.id,
									}))}
									value={addExEqSelected}
									onChange={(e) => {
										setAddExEqSelected(e);
									}}
									isMulti
								/>
							</div>
							<div>

								<div className="mt-2">
									{addExPreviewImg && 
										<div>
											<img src={addExPreviewImg ? URL.createObjectURL(addExPreviewImg) : ""} width="150" height="auto" />
											<br/>
											<Button 
												onClick={()=>{
													setAddImgOperation(false);
													setAddExPreviewImg("");
												}}
												className="text-danger" 
												variant="link" 
												size="sm"
											>
												Remove Image/Gif
											</Button>
										</div>
									}
								</div>
								<div style={{ marginTop: '7px' }}>
									<label htmlFor="newProfilePhoto" className="btn btn-secondary btn-sm fileinput-button">
										Upload Preview Image/Gif(Optional)
									</label>

									<input 
										accept="image/*"
										style={{ display: "none" }} 
										type="file" 
										id="newProfilePhoto" 
										name="profilePicFile"
										onChange={(e) => {
											setAddExPreviewImg(e.target.files[0]);
											setAddImgOperation(true);
										}}
									/>
								</div>
							</div>




							<div className="p-2 mt-3" style={{ backgroundColor: "#e2e2e2" }}>
								<div> <b> Demo Video </b></div>
								<div className="flex-fill">
									<div className="mt-2" >
										<Form>
											<Form.Check className="d-inline-block"
												type="switch"
												id="custom-switch"
												checked={uploadVideoChosen}
												onChange={(e) => {
													setUploadVideoChosen(e.target.checked)
												}}
											/> <span> Upload a video  </span>
											<OverlayTrigger
												placement={"top"}
												overlay={
													<Tooltip id={`tooltip-top`}>
														either upload a video or use an external link
													</Tooltip>
												}
											>
												<i
													className="fas fa-info-circle"
													style={{ color: "#525F7F" }}
												></i>
											</OverlayTrigger>
										</Form>
									</div>

									{!uploadVideoChosen && <div>
										<label className={classes.ModalLabel}>
											External Link (Optional)
										</label>

										<DebounceInput
											minLength={1}
											debounceTimeout={300}
											className="input-group"
											onChange={(e) => setAddExDemoLink(e.target.value)}
											value={addExDemoLink}
										/>
									</div>}
								</div>
								{!isClient && uploadVideoChosen &&

									<Col>
										<Row style={{ marginTop: '20px' }}>
											<label htmlFor="previewVideo" className="btn btn-secondary btn-sm fileinput-button">
												{isUploading ?
													<Spinner animation="border" role="status" />
													: 'Upload Demo Video (Optional, max video size 40MB)'}
											</label>

											<input
												style={{ display: "none" }}
												name="previewVideo"
												ref={addVideoRef}
												type="file"
												className="form-control form-control-alternative p-1"
												id="previewVideo"
												accept="video/*"
												aria-describedby="preview"
												onChange={(e) => {
													if (e.target.files[0].size > 40000000) {
														// console.log(e.target.files[0].size)
														alert("Max video upload size in 40MB")
														e.target.value = ""
													} else {
														setIsUploading(true)
														dispatch(getNumRemainingDemoVideos())
															.then(res => {
																setIsUploading(false)
																if (res.payload.data.num_remaining < 1) {
																	alert("All available demo videos on your current account plan have already been used")
																	e.target.value = ""
																} else {
																	setAddExDemoVideo(e.target.files[0]);
																	setAddVideoOperation(true);
																}
															})
													}
												}}
											/>

										</Row>
										{addExDemoVideo &&
											<>
												<video controls width="250">
													<source src={addExDemoVideo ? URL.createObjectURL(addExDemoVideo) : ""}
														type="video/webm" />
												</video>
												<button className="d-block btn-sm mt-2 btn btn-danger" onClick={() => { setAddExDemoVideo(""); addVideoRef.current.value = "" }}> Cancel Upload </button>
											</>
										}
									</Col>
								}
							</div>






							<div className="flex-fill">
								<label className={classes.ModalLabel}>Notes (Optional)</label>
								{(addExNotes !== "") &&
									<span
										style={{
											backgroundColor: "transparent",
											border: 0,
											outline: 0,
											padding: "0px 6px 0px 0px",
											position: "absolute",
											bottom: "76px",
											right: "32px",
											// height: "100%",
											display: "table-cell",
											verticalAlign: "middle",
											cursor: "pointer"
										}}
										onClick={(e) => { (document.getElementById("addNotesForm").value = ""); setAddExNotes("") }}
									>&#x2612;</span>
								}
								<DebounceInput
									element="textarea"
									minLength={1}
									debounceTimeout={300}
									className="form-control"
									rows="3"
									id="addNotesForm"
									onChange={(e) => setAddExNotes(e.target.value)}
									value={addExNotes}
									placeholder="Keep head up, eyes forward and retract the scapula."
								/>
							</div>
							{(isTrainer && isPartOfGym) && <div className="ml-2">
								<Form.Check
									className="mt-2"
									name="add to gym library"
									id="add-to-gym-library"
									label={'Add to Gym Library'}
									checked={addToGymLibrary}
									onChange={(e) => {
										setAddToGymLibrary(e.target.checked);
									}}
								/>
							</div>}
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="danger" onClick={() => handleAddClose()}>
						Cancel
					</Button>
					<Button
						disabled={isUploading}
						// style={{ backgroundColor: "#5E72E4", outline: "none" }}
						variant="success"
						onClick={async () => {
							let exEqNames = addExEqSelected.map((eq) => eq.label);
							let exEqIds = addExEqSelected.map((eq) => parseInt(eq.value));
							if (
								!addExMuscleGrp.name ||
								!addExMuscleGrp ||
								!addExLevel.name ||
								!addExLevel.id
							) {
								alert("Please specify all required values");
								return;
							}


							const payload = {
								action: "createExercise",
								name: addExName,
								muscle_group_id: addExMuscleGrp.id,
								level_id: addExLevel.id,
								equipment_ids: exEqIds,
								link: uploadVideoChosen ? "" : addExDemoLink,
								notes: addExNotes,
							}
							if (addToGymLibrary) {
								payload.add_to_gym_library = 1;
							}
							if (addImgOperation) {
								setIsUploading(true)
								const s3Res = await processUpload(addExPreviewImg)
								payload.preview_src = s3Res.payload.url;
								payload.preview_using_signed_urls = 1
							}
							if (addVideoOperation && uploadVideoChosen) {
								payload.demo_video = addExDemoVideo;
							}

							statusDispatch(addExercise(payload), "exercise", "create")
							clearAddModalVar();
							handleAddClose(true);
							setIsUploading(false)
						}}
					>
						Save To My Exercise List {isUploading&&<Spinner animation="border" size="sm"/>}
					</Button>
				</Modal.Footer>
			</Modal>

			{/* EDIT MODAL */}

			<Modal show={showEdit} onHide={handleEditClose}>
				<Modal.Header closeButton style={{ margin: 0 }}>
					<Modal.Title>
						Edit Exercise
						<div style={{ fontSize: ".9rem" }}>
							<i className="fas fa-info-circle mr-1 my-2"></i>
							<span className="font-weight-normal">Editing an exercise will affect all workouts using it</span>
						</div>
					</Modal.Title>
				</Modal.Header>

				<Modal.Body style={{ paddingLeft: 30, paddingRight: 30 }}>
					{Object.keys(additionalExerciseData).length > 0 && (
						<>
							<div className="flex-fill">
								<label className={classes.ModalLabel}>Exercise Name</label>
								<DebounceInput
									minLength={1}
									debounceTimeout={300}
									onChange={(e) => setEditExName(e.target.value)}
									value={editExName}
									className="input-group"
								/>
							</div>
							{/* Muscle Filter */}
							<div style={{ position: "relative" }}>
								<div>
									<label
										className={classes.ModalLabel}
										htmlFor="muscle-group-select"
									>
										Muscle Group
									</label>
								</div>

								<Select
									options={additionalExerciseData.muscle_groups.map(
										(muscleGroup) => ({
											label: muscleGroup.name,
											value: JSON.stringify({
												id: muscleGroup.id,
												name: muscleGroup.name,
											})
										})
									)}
									onChange={(e) => {
										setEditExMuscleGrp(JSON.parse(e.value));
									}}
									id="muscle-group-select"
									defaultValue={{ value: JSON.stringify(editExMuscleGrp), label: editExMuscleGrp.name }}

								/>
								{!isClient &&
									<div className="mt-2">
										<Link to="/muscleGroups" target="_blank"
										>
											Add New Muscle Group
											<i
												className="far fa-question-circle text-white"
												style={{ marginLeft: "5px" }}
											></i>
										</Link>
									</div>}
							</div>
							<div className="flex-fill">
								<div>
									<label className={classes.ModalLabel} htmlFor="level-select">
										Levels
									</label>
								</div>
								<Select

									id="level-select"
									options={additionalExerciseData.exercise_levels.map((level) => ({
										label: level.name,
										value:
											JSON.stringify({
												id: level.id,
												name: level.name,
											})
									})
									)}
									onChange={(e) => {
										setEditExLevel(JSON.parse(e.value));
									}}
									defaultValue={{ value: JSON.stringify({ id: editExLevel.exerciseLevelID, name: editExLevel.exerciseLevel }), label: editExLevel.exerciseLevel }}

								/>
							</div>
							{/* Equipment data does not show up in local */}
							<div className="flex-fill">
								<label className={classes.ModalLabel}>Equipment</label>

								<Select
									options={additionalExerciseData.equipment.map((equip) => ({
										label: equip.name,
										value: equip.id,
									}))}
									value={editExEqSelected}
									onChange={(e) => {
										setEditExEqSelected(e);
									}}
									isMulti
								/>
							</div>
							<div className="flex-fill">
								<label className={classes.ModalLabel}>Preview Image</label>
								<div>
									<Row style={{ marginTop: '7px', marginLeft: '0' }}>
										<label htmlFor="preview" className="btn btn-secondary btn-sm fileinput-button">
											Upload Preview Image/Gif
										</label>

										<input 
											style={{ display: "none" }} 
											type="file" 
											id="preview" 
											name="profilePicFile"
											accept="image/*"
											ref={imgRef}
											onChange={(e) => {
												setEditExPreviewImg(e.target.files[0]);
												setEditImgOperation(true);
												setEditExPreviewImgLink(URL.createObjectURL(e.target.files[0]))
											}}
										/>
									</Row>
									<Row>
										{editExPreviewImg &&
											<img
												src={editExPreviewImgLink ? editExPreviewImgLink : editExPreviewImg}
												style={{ width: 'auto', height: 150, marginLeft: '14px' }}
											/>
										}
									</Row>

									{editExPreviewImg && (
										<div>
											{(typeof (editExPreviewImg) === "string") &&
												<button 
													className="mt-2 d-block btn btn-sm btn-danger" 
													onClick={() => { 
														setRemoveExPreviewImg(true); 
														setEditExPreviewImg(""); 
														imgRef.current.value = "" 
													}}
												> 
													Remove Image 
												</button>
											}
										</div>

									)}
								</div>

							</div>

							<div className="p-2 mt-3" style={{ backgroundColor: "#e2e2e2" }}>
								<div> <b> Demo Video </b></div>
								<div className="mt-2" >
									<Form>
										<Form.Check className="d-inline-block"
											type="switch"
											id="custom-switch"
											checked={uploadVideoChosen}
											onChange={(e) => {
												setUploadVideoChosen(e.target.checked)
											}}
										/> <span> Upload a video  </span>
										<OverlayTrigger
											placement={"top"}
											overlay={
												<Tooltip id={`tooltip-top`}>
													either upload a video or use an external link
												</Tooltip>
											}
										>
											<i
												className="fas fa-info-circle"
												style={{ color: "#525F7F" }}
											></i>
										</OverlayTrigger>
									</Form>
								</div>

								{!uploadVideoChosen && <div className="flex-fill">
									<label className={classes.ModalLabel}>External Link</label>

									<DebounceInput
										minLength={1}
										debounceTimeout={300}
										className="input-group"
										onChange={(e) => setEditExDemoLink(e.target.value)}
										value={editExDemoLink}
									/>
								</div>}


								{!isClient && uploadVideoChosen &&
									<Col>
										<Row style={{ marginTop: '20px' }}>
											<label htmlFor="previewEditVideo" className="btn btn-secondary btn-sm fileinput-button">
												{isUploading ?
													<Spinner animation="border" role="status" />
													: 'Upload Demo Video (Optional, max video size 40MB)'}
											</label>

											<input
												style={{ display: "none" }}
												ref={editVideoRef}
												type="file"
												className="p-1 form-control form-control-alternative"
												id="previewEditVideo"
												aria-describedby="preview"
												onChange={(e) => {
													if (e.target.files[0].size > 40000000) {
														// console.log(e.target.files[0].size)
														alert("Max video upload size in 40MB")
														e.target.value = ""
													}
													else {
														setIsUploading(true)
														dispatch(getNumRemainingDemoVideos())
															.then(res => {
																setIsUploading(false)
																if (res.payload.data.num_remaining < 1) {
																	alert("All available demo videos on your current account plan have already been used")
																	e.target.value = ""
																} else {
																	setEditExDemoVideo(e.target.files[0]);
																	setEditVideoOperation(true);
																	setRemoveExDemoVideo(false)
																	setIsUploadedEditVideo(true)
																}
															})
													}
												}}
												accept="video/*"
											/>
										</Row>

										{(editVideoRef?.current?.value && !isUploading && isUploadedEditVideo) ?
											<>
												{editExDemoVideo &&
													<>
														<video controls width="250">

															<source src={editExDemoVideo ? URL.createObjectURL(editExDemoVideo) : ""}
																type="video/webm" />
														</video>
														<button className="d-block btn-sm mt-2 btn btn-danger" onClick={() => { setRemoveExDemoVideo(true); setIsUploadedEditVideo(false); setEditExDemoVideo(''); editVideoRef.current.value = "" }}> Cancel Upload </button>
													</>
												}
											</>
											:
											<>
												{editExDemoVideo &&
													<>
														<video controls width="250">
															<source src={editExDemoVideo ? editExDemoVideo : ""}
																type="video/webm" />
														</video>
														<button className="d-block btn-sm mt-2 btn btn-danger" onClick={() => { setRemoveExDemoVideo(true); setIsUploadedEditVideo(false); setEditExDemoVideo(''); editVideoRef.current.value = "" }}> Remove Demo </button>
													</>
												}
											</>
										}

									</Col>
								}
							</div>

							<div className="flex-fill">
								<label className={classes.ModalLabel}>Notes</label>
								<DebounceInput
									element="textarea"
									minLength={1}
									debounceTimeout={300}
									class="form-control"
									rows="3"
									onChange={(e) => setEditExNotes(e.target.value)}
									value={editExNotes}
								/>
							</div>
							<hr/>
							<div className="flex-fill">
								<label className={classes.ModalLabel}>Update All Workout Notes</label>
								<Form.Check
									onChange={e=>{
										setUpdateAllWorkouts(e.target.checked)
									}}
									checked={updateAllWorkouts}
									id="update-all-workouts-check"
									label="Update All Workouts"
								/>
								<small>
									<i className="fa fa-info-circle mr-1"/>
									Checking this will update all workouts that are using this exercise to have the updated notes.
								</small>
							</div>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button 
						onClick={() => setShowEdit(false)} 
						variant="danger"
					>
						Close
					</Button>
					<Button
						disabled={isUploading}
						variant="success"
						// style={{ backgroundColor: "#5E72E4", border: "none" }}
						onClick={async () => {
							let exEqNames = editExEqSelected.map((eq) => eq.label);
							let exEqIds = editExEqSelected.map((eq) => parseInt(eq.value));

							if ((editExDemoVideo?.length > 0 || editVideoRef?.current?.value) && editExDemoLink.length > 0) {
								alert("Please upload a demo video OR a demo link, not both")
								return
							}

							const payload = {
								id: editExId,
								action: "updateExercise",
								name: editExName,
								muscle_group_id: editExMuscleGrp.id,
								level_id: editExLevel.id,
								equipment_ids: exEqIds,
								link: uploadVideoChosen ? "" : editExDemoLink,
								notes: editExNotes,
								update_all_workouts:Number(updateAllWorkouts)

							}
							if (removeExPreviewImg) {
								payload.delete_preview = 1;
							}

							if (removeExDemoVideo) {
								payload.delete_demo_video = 1;
							}

							if (editImgOperation) {
								setIsUploading(true)
								const s3Res = await processUpload(editExPreviewImg)
								payload.preview_src = s3Res.payload.url
								payload.preview_width = '180px'
								payload.preview_using_signed_urls = 1
							}
							if (editVideoOperation && uploadVideoChosen) {
								payload.demo_video = editExDemoVideo;
							}
							statusDispatch(editExercise(payload), "exercise", "update")
							handleEditClose();
							setIsUploading(false)
						}}
					>
						Save {isUploading&&<Spinner animation="border" size="sm"/>}
					</Button>
				</Modal.Footer>
			</Modal>
			<BulkCopyModal 
				show={showBulkCopyModal}
				onHide={()=>{
					setShowBulkCopyModal(false)
				}}
				onCopySuccess={()=>{
					setSelectedList([])
				}}
				exercise_db_ids={selectedList}
			/>
		</div >
	)
}

export default ExerciseList;
